import React from 'react'
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip } from '../Tooltip'

export interface SortableHeaderButtonProps {
  /** Label text for the column header */
  label: string
  /** Key identifying this sortable column */
  sortKey: string
  /** Currently active sort column key */
  currentSortBy: string
  /** Current sort direction */
  currentSortDirection: 'asc' | 'desc'
  /** Handler called when sort is clicked */
  onSort: (key: string) => void
  /** Whether sorting is disabled (e.g., during loading) */
  disabled?: boolean
  /** Additional class names */
  className?: string
  /** Optional tooltip text to display on hover */
  tooltip?: string
}

export const SortableHeaderButton: React.FC<SortableHeaderButtonProps> = ({
  label,
  sortKey,
  currentSortBy,
  currentSortDirection,
  onSort,
  disabled = false,
  className,
  tooltip,
}) => {
  const isActive = currentSortBy === sortKey

  const getAriaLabel = () => {
    if (!isActive) {
      return `Ordenar por ${label.toLowerCase()}`
    }
    return `Ordenar por ${label.toLowerCase()}, ordem ${currentSortDirection === 'asc' ? 'crescente' : 'decrescente'}`
  }

  const button = (
    <button
      onClick={() => onSort(sortKey)}
      className={cn(
        'flex items-center gap-1 hover:text-violet-600 transition-colors',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      aria-label={getAriaLabel()}
      disabled={disabled}
    >
      {label}
      {isActive ? (
        currentSortDirection === 'asc' ? (
          <ArrowUp className="h-4 w-4" />
        ) : (
          <ArrowDown className="h-4 w-4" />
        )
      ) : (
        <ArrowUpDown className="h-4 w-4 text-gray-400" />
      )}
    </button>
  )

  return tooltip ? (
    <Tooltip content={tooltip} placement="top">
      {button}
    </Tooltip>
  ) : button
}
